import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { SEO } from "@/components/SEO"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Loader2, RefreshCw, AlertCircle, Activity } from "lucide-react"
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
} from "recharts"
import { fetchSpreadSeries, type SpreadSeries, type Timeframe } from "@/lib/spreadData"

const LIMIT_BY_TIMEFRAME: Record<Timeframe, number> = {
  minute: 120,  // 2 hours
  hour: 72,     // 3 days
  day: 30,      // 30 days
}

function fmtSignedPct(pct: number): string {
  const sign = pct > 0 ? "+" : ""
  return `${sign}${pct.toFixed(4)}%`
}

const SUBSCRIPT_DIGITS = ["₀", "₁", "₂", "₃", "₄", "₅", "₆", "₇", "₈", "₉"]

function toSubscript(n: number): string {
  return String(n).split("").map(d => SUBSCRIPT_DIGITS[Number(d)]).join("")
}

function fmtUsdPrice(price: number): string {
  if (!isFinite(price) || price === 0) return "$0"
  const sign = price < 0 ? "-" : ""
  const abs = Math.abs(price)
  if (abs >= 1) return `${sign}$${abs.toFixed(4)}`
  if (abs >= 0.0001) return `${sign}$${abs.toFixed(6)}`

  // "$0.0₇4045" form — subscript = total leading zeros after the decimal
  // before the first non-zero digit.
  const exp = Math.floor(Math.log10(abs))
  const sigStr = (abs / Math.pow(10, exp)).toFixed(3)
  let leadingZeros = -exp - 1
  let digits: string
  if (sigStr.startsWith("10")) {
    leadingZeros -= 1
    digits = "1000"
  } else {
    digits = sigStr.replace(".", "")
  }
  return `${sign}$0.0${toSubscript(leadingZeros)}${digits}`
}

function fmtTs(ts: number, timeframe: Timeframe): string {
  const d = new Date(ts * 1000)
  if (timeframe === "day") return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
  if (timeframe === "hour") {
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit" })
  }
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
}

export function SpreadPage() {
  const [timeframe, setTimeframe] = useState<Timeframe>("hour")
  const [series, setSeries] = useState<SpreadSeries | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const cancelledRef = useRef(false)

  const load = useCallback(async (tf: Timeframe) => {
    setLoading(true)
    setError(null)
    try {
      const next = await fetchSpreadSeries(tf, LIMIT_BY_TIMEFRAME[tf])
      if (cancelledRef.current) return
      setSeries(next)
      setLastUpdated(Date.now())
    } catch (e) {
      if (cancelledRef.current) return
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      if (!cancelledRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    cancelledRef.current = false
    load(timeframe)
    const iv = setInterval(() => load(timeframe), 60_000)
    return () => {
      cancelledRef.current = true
      clearInterval(iv)
    }
  }, [timeframe, load])

  const chartData = useMemo(() => {
    if (!series) return []
    return series.points.map(p => ({
      ts: p.ts,
      label: fmtTs(p.ts, series.timeframe),
      spreadPct: Number(p.spreadPct.toFixed(6)),
      priceBusd: p.priceBusd,
      priceViaWbnb: p.priceViaWbnb,
    }))
  }, [series])

  const latest = series?.latest ?? null
  const directionHint = latest
    ? latest.spreadPct > 0
      ? "ELE cheaper in pairBUSD — DIR 0 (buy pairBUSD, sell pairWBNB)"
      : latest.spreadPct < 0
        ? "ELE cheaper in pairWBNB — DIR 1 (buy pairWBNB, sell pairBUSD)"
        : "Pools balanced"
    : null

  const latestColor = latest == null
    ? "text-muted-foreground"
    : Math.abs(latest.spreadPct) >= 0.75
      ? "text-green-600 dark:text-green-400"
      : latest.spreadPct === 0
        ? "text-muted-foreground"
        : "text-amber-600 dark:text-amber-400"

  return (
    <>
      <SEO title="ELEPHANT Pool Spread" noindex />
      <div className="container mx-auto max-w-5xl px-4 py-6 space-y-6">
        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Activity className="h-5 w-5" />
            ELEPHANT Pool Spread
          </h1>
          <p className="text-sm text-muted-foreground">
            Historical divergence between ELEPHANT/BUSD and ELEPHANT/WBNB pools. Data from GeckoTerminal.
            Positive spread = ELE cheaper in pairBUSD; negative = cheaper in pairWBNB. The 0.75% band
            (shaded) is the 3×0.25% PCS fee floor below which no arb can clear fees.
          </p>
        </header>

        <Card>
          <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div>
              <div className="text-sm text-muted-foreground">Current spread</div>
              <div className={`text-4xl font-semibold ${latestColor}`}>
                {latest ? fmtSignedPct(latest.spreadPct) : loading ? "…" : "—"}
              </div>
              {latest && (
                <>
                  <div className="text-xs text-muted-foreground mt-1">{directionHint}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    priceBusd {fmtUsdPrice(latest.priceBusd)} · priceViaWbnb {fmtUsdPrice(latest.priceViaWbnb)}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">
                {lastUpdated ? `updated ${fmtTs(Math.floor(lastUpdated / 1000), "minute")}` : ""}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => load(timeframe)}
                disabled={loading}
              >
                {loading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <RefreshCw className="h-4 w-4" />}
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center gap-4">
          <Tabs value={timeframe} onValueChange={(v) => setTimeframe(v as Timeframe)}>
            <TabsList>
              <TabsTrigger value="minute">Minute</TabsTrigger>
              <TabsTrigger value="hour">Hour</TabsTrigger>
              <TabsTrigger value="day">Day</TabsTrigger>
            </TabsList>
          </Tabs>
          <span className="text-xs text-muted-foreground">
            {series ? `${series.points.length} points` : ""}
          </span>
        </div>

        {error && (
          <Card className="border-destructive">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <div className="flex-1 text-sm">
                <div className="font-medium text-destructive">Failed to load spread data</div>
                <div className="text-muted-foreground text-xs">{error}</div>
              </div>
              <Button variant="outline" size="sm" onClick={() => load(timeframe)}>Retry</Button>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-4">Spread over time (%)</h3>
            <div className="h-[320px]">
              {chartData.length === 0 && !loading && !error && (
                <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                  No data for this timeframe.
                </div>
              )}
              {chartData.length > 0 && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      width={55}
                      tickFormatter={(v: number) => `${v.toFixed(2)}%`}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        color: "hsl(var(--card-foreground))",
                      }}
                      labelStyle={{ color: "hsl(var(--card-foreground))" }}
                      itemStyle={{ color: "hsl(var(--card-foreground))" }}
                      labelFormatter={(label) => String(label)}
                      formatter={(value, name) => {
                        if (name === "spreadPct") return [fmtSignedPct(Number(value)), "spread"]
                        if (name === "priceBusd") return [fmtUsdPrice(Number(value)), "priceBusd"]
                        if (name === "priceViaWbnb") return [fmtUsdPrice(Number(value)), "priceViaWbnb"]
                        return [value, name]
                      }}
                    />
                    {/* 0.75% fee-floor band */}
                    <ReferenceLine y={0.75} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 2" opacity={0.5} />
                    <ReferenceLine y={-0.75} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 2" opacity={0.5} />
                    <ReferenceLine y={0} stroke="hsl(var(--foreground))" opacity={0.3} />
                    <Line
                      type="monotone"
                      dataKey="spreadPct"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-muted-foreground mb-4">Pool prices (BUSD per ELE)</h3>
            <div className="h-[260px]">
              {chartData.length > 0 && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      width={70}
                      domain={["auto", "auto"]}
                      tickFormatter={(v: number) => fmtUsdPrice(v)}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        color: "hsl(var(--card-foreground))",
                      }}
                      labelStyle={{ color: "hsl(var(--card-foreground))" }}
                      itemStyle={{ color: "hsl(var(--card-foreground))" }}
                      formatter={(value, name) => [fmtUsdPrice(Number(value)), name]}
                    />
                    <Line
                      type="monotone"
                      dataKey="priceBusd"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                      name="priceBusd"
                    />
                    <Line
                      type="monotone"
                      dataKey="priceViaWbnb"
                      stroke="hsl(var(--destructive))"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                      name="priceViaWbnb"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
